import React from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { fmtCurrency, fmtSignedCurrency } from '../../lib/display-labels';
import type { InvoiceTaxWarning } from '../../lib/invoice-tax-warning';

/**
 * The API's TAX_DIFFERS_FROM_TICKETS warning, shown on the generate result.
 *
 * A warning, not an error and not a success: the invoice exists. Amber is
 * carried by the panel's left edge and the icon's filled chip. Every word is in
 * the theme-following ink tokens, because a signal hue used as text drops
 * under 3:1 on the light card. The figures are the API's, formatted only;
 * an absent one reads as an em dash.
 */
export function InvoiceTaxWarningNotice({
  warning,
  onDismiss,
}: {
  warning: InvoiceTaxWarning;
  onDismiss: () => void;
}) {
  return (
    <div
      role="status"
      data-testid="invoice-tax-warning"
      className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card p-4 text-fieldiq-text"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-fi-signal-amber text-fi-signal-ink"
        >
          <AlertTriangle size={15} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold">Tax differs from the approved tickets</h2>
          {warning.message && <p className="mt-1 text-sm">{warning.message}</p>}
          <dl className="mt-3 grid grid-cols-3 gap-2 text-sm">
            <div>
              <dt className="text-xs text-fieldiq-muted">Tickets</dt>
              <dd className="font-semibold tabular-nums">{fmtCurrency(warning.ticketTotal)}</dd>
            </div>
            <div>
              <dt className="text-xs text-fieldiq-muted">Invoice</dt>
              <dd className="font-semibold tabular-nums">{fmtCurrency(warning.invoiceTotal)}</dd>
            </div>
            <div>
              <dt className="text-xs text-fieldiq-muted">Difference</dt>
              <dd className="font-semibold tabular-nums">{fmtSignedCurrency(warning.difference)}</dd>
            </div>
          </dl>
          <p className="mt-3 text-xs text-fieldiq-muted">
            Tickets:{' '}
            <span className="text-fieldiq-text">
              {warning.ticketNumbers.length > 0 ? warning.ticketNumbers.join(', ') : '—'}
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss tax warning"
          className="shrink-0 rounded p-1 text-fieldiq-muted hover:bg-fieldiq-dark/50 hover:text-fieldiq-text"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

export default InvoiceTaxWarningNotice;
